// @ts-nocheck
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useLocalization, type Language } from '../../../config/localization';
import Icon, { ICONS } from '../../ui/icons';
import AudioWaveformPlaceholder from '../../ui/AudioWaveformPlaceholder';
import Model3DPlaceholder from '../../ui/displays/Model3DPlaceholder';
import LazyModel3DViewer from '../../ui/displays/LazyModel3DViewer';
import MediaPreviewModal from '../../ui/overlays/modals/MediaPreviewModal';
import MarkButtons, { type AssetMark } from '../../ui/displays/MarkButtons';
import CollectionActionsMenu, { type CollectionActionsMenuRef } from './CollectionActionsMenu';
import { getProxiedImageUrl, getOriginalImageUrl, getThumbnailUrl } from '../../../utils/imageProxy';
import type { GalleryImage } from './GalleryContent';
import type { GenerationSlot } from '../../../contexts/WidgetContext';
import { galleryImageToSlot, inferContentTypeFromUrl as inferContentType, findModel3DUrls, findPosterImageUrl, extractGenerationParams } from './galleryUtils';
import { isUnviewableModelUrl, resolveIs3D } from '../../../utils/mediaTypeDetection';
import { buildDownloadFilename } from '../../../utils/downloadNaming';
import { resolveRecreateRoute } from '../../../utils/recreateRoute';
import { convertBlobToWebP } from '../../../utils/imageWebp';

interface Collection {
  id: number;
  name: string;
  description: string | null;
  is_default: boolean;
  is_private: boolean;
  can_edit: boolean;
  total_images?: number;
}

interface GalleryImageCardProps {
  image: GalleryImage;
  index: number;
  viewMode: 'grid' | 'list';
  language: Language;
  organizationId: number;
  selectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelection?: (id: number, index: number, shiftKey: boolean) => void;
  onDragSelectStart?: (id: number, index: number) => void;
  onDragSelectEnter?: (id: number) => void;
  isDragSelecting?: boolean;
  onMoveToTrash?: (id: number) => void;
  onMark?: (id: number, mark: AssetMark | null) => void;
  onRestore?: (id: number) => void;
  isTrashMode?: boolean;
  // Collection actions
  collections?: Collection[];
  onAddToCollections?: (imageId: number, collectionIds: number[]) => Promise<void>;
  onRemoveFromCollection?: (imageId: number, collectionId: number) => Promise<void>;
  // Toast notification
  onShowToast?: (message: string, type: 'success' | 'error') => void;
  // Lightbox callback — when provided, card delegates preview to parent instead of rendering its own modal
  onImageClick?: (index: number) => void;
}


const GalleryImageCard: React.FC<GalleryImageCardProps> = ({
  image,
  index,
  viewMode,
  language,
  organizationId,
  selectionMode = false,
  isSelected = false,
  onToggleSelection,
  onDragSelectStart,
  onDragSelectEnter,
  isDragSelecting = false,
  onMoveToTrash,
  onMark,
  onRestore,
  isTrashMode = false,
  collections = [],
  onAddToCollections,
  onRemoveFromCollection,
  onShowToast,
  onImageClick
}) => {
  const t = useLocalization(language);
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [imageError, setImageError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showHoverViewer, setShowHoverViewer] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [webPDownloadSuccess, setWebPDownloadSuccess] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const contextMenuRef = useRef<CollectionActionsMenuRef>(null);
  const hoverViewerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const supportsHoverViewer = useMemo(
    () => typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(hover: hover) and (pointer: fine)').matches,
    []
  );

  // Detect content type from image data or URLs
  const contentType = image.contentType || inferContentType(
    image.preview_urls || image.original_urls || image.external_urls
  );
  // Support both 'video' and 'video/mp4' formats
  const isVideo = contentType?.startsWith('video');
  const isAudio = contentType?.startsWith('audio');
  // URL fallback as in `GenerationSlot` / `MediaPreviewModal`: the gallery API
  // returns `content_type || 'image'`, so the inference above never sees an
  // empty column here. Without the URL check a model with a missing or stale
  // content type would be routed through `getThumbnailUrl` (a CF Images
  // transform) and render as a broken thumbnail.
  // Scan original → external → preview arrays for the model itself. Providers
  // can put a poster before the mesh in one array, so selecting only the first
  // URL would classify the generation as an image and open/download the poster.
  const model3dUrls = findModel3DUrls(
    contentType,
    image.original_urls,
    image.external_urls,
    image.preview_urls,
  );
  const model3dUrl = model3dUrls[0] ?? null;
  // Per-asset, not per-generation: a 3D result also ships texture/preview
  // images that inherit the same `content_type`. See `resolveIs3D`.
  const is3D = resolveIs3D(contentType, model3dUrl);
  const posterUrl = is3D
    ? findPosterImageUrl(image.preview_urls, image.original_urls, image.external_urls)
    : null;
  const posterGridUrl = posterUrl ? getThumbnailUrl(posterUrl, 600) : null;
  const posterListUrl = posterUrl ? getThumbnailUrl(posterUrl, 100) : null;
  const canShowHoverViewer = viewMode === 'grid'
    && is3D
    && Boolean(model3dUrl)
    && !isUnviewableModelUrl(model3dUrl)
    && supportsHoverViewer
    && image.status === 'success'
    && !selectionMode
    && !isDragSelecting;
  const isText = contentType === 'text';

  const clearHoverViewerTimer = () => {
    if (hoverViewerTimerRef.current !== null) {
      clearTimeout(hoverViewerTimerRef.current);
      hoverViewerTimerRef.current = null;
    }
  };

  useEffect(() => () => clearHoverViewerTimer(), []);

  useEffect(() => {
    if (!canShowHoverViewer) {
      clearHoverViewerTimer();
      setShowHoverViewer(false);
    }
  }, [canShowHoverViewer]);

  // Get text content from image data or raw_response_json fallback
  const getTextContent = (): string => {
    if (image.text_content) return image.text_content;
    // Fallback: extract from raw_response_json
    try {
      const response = JSON.parse(image.raw_response_json);
      if (response?.data?.outputs?.text) return response.data.outputs.text;
      if (response?.outputs?.text) return response.outputs.text;
      if (typeof response === 'string') return response;
    } catch {
      // ignore
    }
    return '';
  };

  // Parse ALL URLs from JSON strings - for grid display
  const getAllImageUrls = (): string[] => {
    // The URL columns contain alternate representations of the same assets.
    // For 3D cards with models, count and navigate only model assets:
    // poster/texture companions are display inputs, not separate generated
    // results. If model delivery failed, retain the ordinary URL fallback so a
    // surviving poster can still open in the modal.
    if (is3D && model3dUrls.length > 0) return model3dUrls;

    try {
      let urls: string[] = [];
      if (image.preview_urls) {
        const parsed = JSON.parse(image.preview_urls);
        if (Array.isArray(parsed)) urls = parsed;
      }
      if (urls.length === 0 && image.original_urls) {
        const parsed = JSON.parse(image.original_urls);
        if (Array.isArray(parsed)) urls = parsed;
      }
      if (urls.length === 0 && image.external_urls) {
        const parsed = JSON.parse(image.external_urls);
        if (Array.isArray(parsed)) urls = parsed;
      }
      // Don't proxy video/audio/3D URLs - they need direct access
      // Use thumbnail URLs for gallery cards (600px for larger card sizes)
      return urls.map(url => (isVideo || isAudio || is3D) ? url : getThumbnailUrl(url, 600));
    } catch (error) {
      console.error('Error parsing media URLs:', error);
    }
    return [];
  };

  // Parse URLs from JSON strings - prioritize preview for thumbnail (first URL only)
  const getImageUrl = () => {
    const urls = getAllImageUrls();
    return urls.length > 0 ? urls[0] : null;
  };

  // Get count of images in this generation
  const getImageCount = (): number => {
    try {
      if (image.preview_urls) {
        const parsed = JSON.parse(image.preview_urls);
        if (Array.isArray(parsed)) return parsed.length;
      }
      if (image.original_urls) {
        const parsed = JSON.parse(image.original_urls);
        if (Array.isArray(parsed)) return parsed.length;
      }
      if (image.external_urls) {
        const parsed = JSON.parse(image.external_urls);
        if (Array.isArray(parsed)) return parsed.length;
      }
    } catch (error) {
      console.error('Error counting images:', error);
    }
    return 1;
  };

  // Get original URL for modal - prioritize original over preview
  const getOriginalImageUrl = () => {
    if (is3D && model3dUrl) return model3dUrl;
    try {
      if (image.original_urls) {
        const urls = JSON.parse(image.original_urls);
        return Array.isArray(urls) && urls.length > 0 ? urls[0] : null;
      }
      if (image.external_urls) {
        const urls = JSON.parse(image.external_urls);
        return Array.isArray(urls) && urls.length > 0 ? urls[0] : null;
      }
      if (image.preview_urls) {
        const urls = JSON.parse(image.preview_urls);
        return Array.isArray(urls) && urls.length > 0 ? urls[0] : null;
      }
    } catch (error) {
      console.error('Error parsing original image URLs:', error);
    }
    return null;
  };

  // Get ALL original URLs for modal gallery
  const getAllOriginalUrls = (): string[] => {
    try {
      if (image.original_urls) {
        const urls = JSON.parse(image.original_urls);
        if (Array.isArray(urls) && urls.length > 0) return urls;
      }
      if (image.external_urls) {
        const urls = JSON.parse(image.external_urls);
        if (Array.isArray(urls) && urls.length > 0) return urls;
      }
      if (image.preview_urls) {
        const urls = JSON.parse(image.preview_urls);
        if (Array.isArray(urls) && urls.length > 0) return urls;
      }
    } catch (error) {
      console.error('Error parsing all original URLs:', error);
    }
    return [];
  };

  // Parse prompt from raw_input
  const getPrompt = () => {
    try {
      const input = JSON.parse(image.raw_input);

      // Try different possible structures
      if (input.prompt) return input.prompt;
      if (input.prompt) return input.prompt; // Handle typo in the data - "prompt" instead of "prompt"
      if (input.text) return input.text;
      if (input.inputs?.prompt) return input.inputs.prompt;
      if (input.inputs?.prompt) return input.inputs.prompt; // Also check for typo in nested structure
      if (input.inputs?.text) return input.inputs.text;
      if (typeof input === 'string') return input;

      return 'No prompt';
    } catch (error) {
      console.error('Error parsing prompt data:', error);
      return 'Invalid prompt data';
    }
  };

  // Handle video playback on hover (YouTube-style)
  useEffect(() => {
    if (isVideo && videoRef.current) {
      if (isHovered) {
        videoRef.current.play().catch(err => console.warn('Video autoplay failed:', err));
      } else {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  }, [isHovered, isVideo]);

  // Multi-image slideshow state
  const [slideIndex, setSlideIndex] = useState(0);

  // Slideshow effect for multi-image cards on hover
  useEffect(() => {
    const allUrls = getAllImageUrls();
    const hasMultiple = allUrls.length > 1;

    if (!isHovered || !hasMultiple || isVideo) {
      setSlideIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setSlideIndex(prev => (prev + 1) % allUrls.length);
    }, 1000);

    return () => clearInterval(interval);
  }, [isHovered, isVideo]);

  // Handle image actions
  const handleDownload = async (e?: React.MouseEvent) => {
    e?.stopPropagation();

    // Text content: download as .txt file
    if (isText) {
      const textContent = getTextContent();
      if (textContent) {
        const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `generated-text-${image.id}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
        setDownloadSuccess(true);
        setTimeout(() => setDownloadSuccess(false), 2000);
      }
      return;
    }

    // Get the original URL for download (not proxied)
    const originalUrl = getOriginalImageUrl();
    if (originalUrl) {
      try {
        // Shared naming helper — same scheme as the slot and the preview modal,
        // so one asset never downloads under two different names.
        const fileName = buildDownloadFilename(contentType, image.id, originalUrl);

        const response = await fetch(originalUrl);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
        setDownloadSuccess(true);
        setTimeout(() => setDownloadSuccess(false), 2000);
      } catch {
        // Fallback: open in new tab if fetch fails (e.g. CORS)
        window.open(originalUrl, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const handleDownloadWebP = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    const originalUrl = getOriginalImageUrl();
    if (originalUrl) {
      try {
        const response = await fetch(originalUrl);
        const blob = await response.blob();
        const webpBlob = await convertBlobToWebP(blob, 0.9);
        const blobUrl = URL.createObjectURL(webpBlob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `image-${image.id}.webp`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
        setWebPDownloadSuccess(true);
        setTimeout(() => setWebPDownloadSuccess(false), 2000);
      } catch {
        window.open(originalUrl, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const handleCopyPrompt = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    const prompt = getPrompt();
    navigator.clipboard.writeText(prompt);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = () => {
    switch (image.status) {
      case 'success': return 'text-green-400';
      case 'failed': return 'text-red-400';
      case 'pending': return 'text-yellow-400';
      default: return 'text-gray-400';
    }
  };

  const getUrlTypeLabel = () => {
    switch (image.url_type) {
      case 0: return t.galleryPage?.preview || 'Preview';
      case 1: return t.galleryPage?.original || 'Original';
      case 2: return t.galleryPage?.external || 'External';
      case 3: return t.galleryPage?.mixed || 'Mixed';
      default: return 'Unknown';
    }
  };

  const allUrls = getAllImageUrls();
  const imageCount = allUrls.length || 1;
  const hasMultipleImages = imageCount > 1;

  // Use slideIndex for multi-image slideshow, otherwise first URL
  const imageUrl = hasMultipleImages && isHovered
    ? allUrls[slideIndex] || allUrls[0]
    : allUrls[0] || null;
  const prompt = getPrompt();
  const hasPrompt = prompt && prompt !== 'No prompt';

  // Extract up to 3 compact generation parameter tags for card display
  const compactParams = React.useMemo(() => {
    const allParams = extractGenerationParams(image.raw_input, {
      booleanTrue: t.galleryPage?.booleanYes || 'Yes',
      booleanFalse: t.galleryPage?.booleanNo || 'No',
    });
    return allParams.slice(0, 3);
  }, [image.raw_input, t.galleryPage?.booleanYes, t.galleryPage?.booleanNo]);

  // Handle image click for preview - defined before use to avoid temporal dead zone
  const handleImageClick = (e: React.MouseEvent) => {
    // In selection mode, only handle shift-click for range selection
    // Normal selection is handled by mousedown for drag support
    if (selectionMode) {
      if (e.shiftKey && onToggleSelection) {
        onToggleSelection(image.id, index, true);
      }
      // Normal clicks are handled by mousedown
      return;
    }
    if (image.status === 'success') {
      if (imageUrl || model3dUrl || posterUrl || isText) {
        clearHoverViewerTimer();
        setShowHoverViewer(false);
        if (onImageClick) {
          onImageClick(index);
        } else {
          setShowPreview(true);
        }
      }
    }
  };

  // Handle card mousedown for drag selection (works anywhere on card in selection mode)
  const handleCardMouseDown = (e: React.MouseEvent) => {
    if (!selectionMode) return;
    if (e.button !== 0) return; // only left mouse button

    // For shift-click, let the click handler deal with it
    if (e.shiftKey) return;

    e.preventDefault();

    if (onDragSelectStart) {
      onDragSelectStart(image.id, index);
    }
  };

  // Handle mouse enter for drag selection
  const handleSelectionMouseEnter = () => {
    if (isDragSelecting && onDragSelectEnter) {
      onDragSelectEnter(image.id);
    }
  };

  const handleCardMouseEnter = () => {
    setIsHovered(true);
    handleSelectionMouseEnter();

    if (canShowHoverViewer) {
      clearHoverViewerTimer();
      hoverViewerTimerRef.current = setTimeout(() => {
        hoverViewerTimerRef.current = null;
        setShowHoverViewer(true);
      }, 300);
    }
  };

  const handleCardMouseLeave = () => {
    setIsHovered(false);
    clearHoverViewerTimer();
    setShowHoverViewer(false);
  };

  // Handle trash button click
  const handleTrashClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (onMoveToTrash) {
      onMoveToTrash(image.id);
    }
  };

  // Handle restore button click
  const handleRestoreClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (onRestore) {
      onRestore(image.id);
    }
  };

  // Handle recreate - navigate to generation page with same settings
  const handleRecreate = async () => {
    try {
      // 1. Check if workflow is available
      const token = await getToken({ template: 'withmail' });
      const response = await fetch(`/api/user/workflows/${image.workflow_id}/details`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Organization-Id': organizationId.toString()
        }
      });

      if (!response.ok) {
        // Workflow not available
        onShowToast?.(t.gallery?.workflowUnavailable || 'Workflow is unavailable or has been deleted', 'error');
        return;
      }

      const workflowDetails = await response.json();

      // 2. Parse raw_input and external_urls
      let rawInput: Record<string, unknown> = {};
      try {
        rawInput = JSON.parse(image.raw_input || '{}');
      } catch (e) {
        console.warn('Failed to parse raw_input:', e);
      }

      // Get INPUT image URLs from raw_input (not external_urls which are OUTPUT images)
      // These are the images that were used as input for the generation
      let mediaUrls: string[] = [];
      try {
        // Check for image_urls in rawInput (saved by backend in raw_input)
        if (rawInput.image_urls && Array.isArray(rawInput.image_urls)) {
          mediaUrls = rawInput.image_urls;
        } else if (rawInput.video_urls && Array.isArray(rawInput.video_urls)) {
          mediaUrls = rawInput.video_urls;
        } else if (rawInput.audio_urls && Array.isArray(rawInput.audio_urls)) {
          mediaUrls = rawInput.audio_urls;
        }
      } catch (e) {
        console.warn('Failed to get input media URLs for recreate:', e);
      }

      // 3. Write dynamic widget values to localStorage BEFORE navigation
      // This ensures EmbeddedDynamicWidgetRenderer reads correct values on mount
      const standardFields = ['prompt', 'text', 'num_images', 'gen_count',
        'guidance_scale', 'safety_tolerance', 'num_inference_steps'];

      for (const [fieldId, value] of Object.entries(rawInput)) {
        if (!standardFields.includes(fieldId) && value !== undefined && value !== null) {
          try {
            const storageKey = `embedded_widget_${image.workflow_id}_${fieldId}_${organizationId}`;
            localStorage.setItem(storageKey, JSON.stringify(value));
          } catch (e) {
            console.warn(`Failed to save recreate value for ${fieldId}:`, e);
          }
        }
      }

      // 4. Store basic settings in sessionStorage for ImageGenerationContext
      const recreateSettings = {
        workflowId: image.workflow_id,
        rawInput,
        mediaUrls,
        // Stamped for the same reason as `recreate-prompt-v2` below — see
        // `src/utils/recreateStorage.ts`.
        organizationId,
        timestamp: Date.now()
      };

      sessionStorage.setItem('recreate-settings', JSON.stringify(recreateSettings));

      // 5. Store raw input separately for DynamicWidgetRenderer (loads async after fields fetch)
      // This key persists longer as DynamicWidgetRenderer needs time to load fields via API
      sessionStorage.setItem('recreate-dynamic-values', JSON.stringify({
        rawInput,
        organizationId,
        timestamp: Date.now()
      }));

      // 6. Store prompt_v2 specifically for PromptEditorV2Widget restoration
      // Check both direct path and nested path (inside inputs object)
      // Always clear first to prevent stale data from a previous recreate
      sessionStorage.removeItem('recreate-prompt-v2');
      const promptV2Data = rawInput.prompt_v2 || rawInput.inputs?.prompt_v2;
      if (promptV2Data) {
        sessionStorage.setItem('recreate-prompt-v2', JSON.stringify({
          promptV2: promptV2Data,
          workflowId: image.workflow_id,
          // The blob carries this organization's reference bodies, prompt ids and
          // media URLs. PromptEditorV2Widget rejects it unless the org still
          // matches, so a switch between Recreate and the editor loading can't
          // seed a generation billed to another org with this org's references.
          organizationId,
          timestamp: Date.now()
        }));
      }

      // 7. Store media URLs separately for Slots widgets
      // Always clear first to prevent stale data from a previous recreate
      sessionStorage.removeItem('recreate-media-urls');
      if (mediaUrls.length > 0) {
        // These URLs become the input media of a BILLED generation, so the blob
        // is org-stamped like the others — the media widgets reject a foreign
        // one instead of feeding another tenant's assets into this generation.
        // See `src/utils/recreateStorage.ts`.
        sessionStorage.setItem('recreate-media-urls', JSON.stringify({
          mediaUrls,
          organizationId,
          timestamp: Date.now()
        }));
      }

      // 7b. Store per-slot data for multi-slot workflows
      // Always clear first to prevent stale data from a previous recreate
      sessionStorage.removeItem('recreate-slots-data');
      // Extract slot config from workflow widgets (slots_input_media widget, type_id 10)
      try {
        const slotsWidget = (workflowDetails.widgets || []).find(
          (w: { widget_type_id: number }) => w.widget_type_id === 10
        );
        if (slotsWidget?.config_data) {
          const slotsConfig = typeof slotsWidget.config_data === 'string'
            ? JSON.parse(slotsWidget.config_data)
            : slotsWidget.config_data;
          const slots = slotsConfig?.slots;
          if (Array.isArray(slots) && slots.length > 0) {
            const slotMappings: { slotId: string; urls: string[] }[] = [];
            for (const slot of slots) {
              const slotValue = rawInput[slot.slot_id] ?? (rawInput.inputs as Record<string, unknown>)?.[slot.slot_id];
              if (slotValue) {
                const urls = Array.isArray(slotValue) ? slotValue : [slotValue];
                slotMappings.push({ slotId: slot.slot_id, urls: urls.filter((u: unknown) => typeof u === 'string') });
              }
            }
            if (slotMappings.length > 0) {
              // Same org data as `recreate-media-urls` above, just keyed per
              // slot — stamped for the same reason.
              sessionStorage.setItem('recreate-slots-data', JSON.stringify({
                slotMappings,
                organizationId,
                timestamp: Date.now()
              }));
            }
          }
        }
      } catch (e) {
        console.warn('Failed to extract per-slot data for recreate:', e);
      }

      // 8. Store collection IDs for pre-selection after recreate
      // Always clear first to prevent stale data from a previous recreate on parse failure
      sessionStorage.removeItem('recreate-collections');
      try {
        const collectionsJson = image.collections || '[]';
        const collections: { id: number; name: string; is_default: boolean }[] = JSON.parse(collectionsJson);
        const collectionIds = collections.map(c => c.id).filter(id => typeof id === 'number');
        // Always store collection IDs — empty array signals "no collections" to clear existing selections
        // Collection ids live in a per-organization id space, so the blob is
        // org-stamped like the others (see `src/utils/recreateStorage.ts`).
        sessionStorage.setItem('recreate-collections', JSON.stringify({
          collectionIds,
          organizationId,
          timestamp: Date.now()
        }));
      } catch (e) {
        console.warn('Failed to extract collections for recreate:', e);
      }

      // 9. Determine route based on workflow category
      const route = resolveRecreateRoute(workflowDetails.workflow?.category);

      // 10. Navigate with recreate flag
      navigate(`${route}?workflow=${image.workflow_id}&recreate=true`);

    } catch (error) {
      console.error('Failed to recreate:', error);
      onShowToast?.(t.gallery?.workflowUnavailable || 'Failed to recreate generation', 'error');
    }
  };

  // Handle favorite toggle (placeholder for now)
  const handleToggleFavorite = (id: string) => {
    console.log('Toggle favorite for:', id);
    // TODO: Implement favorite functionality for gallery
  };

  // Handle right-click context menu
  const handleContextMenu = (e: React.MouseEvent) => {
    // Don't show context menu in selection mode or trash mode
    if (selectionMode || isTrashMode) return;
    // Only show for successful images with collection handlers
    if (image.status !== 'success' || !onAddToCollections || !onRemoveFromCollection) return;

    e.preventDefault();
    e.stopPropagation();
    contextMenuRef.current?.openAtPosition(e.clientX, e.clientY);
  };

  // Convert GalleryImage to GenerationSlot format for the modal
  const convertToGenerationSlot = (): GenerationSlot | null => {
    return galleryImageToSlot(image);
  };

  if (viewMode === 'list') {
    return (
      <>
        <div
          className={`flex items-center gap-4 p-4 bg-gray-800/30 rounded-lg transition-colors cursor-pointer select-none ${
            isSelected ? 'ring-2 ring-purple-500' : ''
          } ${isDragSelecting ? 'cursor-crosshair' : ''}`}
          onMouseEnter={handleCardMouseEnter}
          onMouseLeave={handleCardMouseLeave}
          onClick={handleImageClick}
          onMouseDown={handleCardMouseDown}
          onContextMenu={handleContextMenu}
        >
          {/* Selection checkbox - visual indicator only */}
          {selectionMode && (
            <div className="flex-shrink-0 pointer-events-none">
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                isSelected
                  ? 'bg-purple-500 border-purple-500'
                  : 'bg-gray-800/80 border-gray-400'
              }`}>
                {isSelected && (
                  <Icon name={ICONS.CHECK} size={12} className="text-white" />
                )}
              </div>
            </div>
          )}
          {/* Thumbnail - clickable */}
          <div className="relative w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-gray-700">
            {is3D ? (
              posterListUrl && !imageError ? (
                <>
                  <img
                    src={posterListUrl}
                    alt={prompt}
                    onError={() => setImageError(true)}
                    className="w-full h-full object-cover"
                  />
                  <span className="absolute bottom-1 right-1 px-1 py-0.5 bg-black/60 text-cyan-300 rounded text-[9px] font-medium">
                    {t.model3d?.badge || '3D'}
                  </span>
                </>
              ) : (
                <Model3DPlaceholder url={model3dUrl} language={language} compact />
              )
            ) : imageUrl && !imageError ? (
              isVideo ? (
                <video
                  ref={videoRef}
                  src={imageUrl}
                  className="w-full h-full object-cover"
                  muted
                  loop
                  playsInline
                  preload="metadata"
                />
              ) : isAudio ? (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900/40 to-gray-800">
                  <AudioWaveformPlaceholder size="small" />
                </div>
              ) : (
                <img
                  src={imageUrl}
                  alt={prompt}
                  onError={() => setImageError(true)}
                  className="w-full h-full object-cover"
                />
              )
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Icon name={isText ? ICONS.TEXT : isVideo ? ICONS.VIDEO : isAudio ? ICONS.AUDIO : ICONS.IMAGE} size={20} className="text-gray-500" />
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white truncate">
                  {prompt}
                </div>
                {/* Combined date and creator info */}
                <div className="text-xs text-gray-400 mt-1">
                  {formatDate(image.created_at)}
                  {image.user_email && (
                    <span> • by {image.user_email}</span>
                  )}
                  {image.generation_count > 1 && !isText && (
                    <span> • {image.generation_count} images</span>
                  )}
                </div>
                {/* Compact parameter tags - always visible in list view */}
                {compactParams.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {compactParams.map(param => (
                      <span
                        key={param.key}
                        className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-700/60 text-[10px] text-gray-300"
                      >
                        {param.label}: {param.value}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Actions */}
              {isHovered && image.status === 'success' && !selectionMode && (
                <div className="ml-4 flex-shrink-0 flex items-center gap-2">
                  {isTrashMode && onRestore && (
                    <button
                      onClick={handleRestoreClick}
                      className="p-1 rounded bg-green-600/20 text-green-400 hover:bg-green-600/30 transition-colors"
                      title={t.gallery?.restore || 'Restore'}
                    >
                      <Icon name={ICONS.REFRESH} size={12} />
                    </button>
                  )}
                  {!isTrashMode && onMark && (
                    <MarkButtons
                      mark={image.mark}
                      onMark={(next) => onMark(image.id, next)}
                      language={language}
                      size={14}
                    />
                  )}
                  {/* Actions Menu - contains copy prompt, download, collections, and trash */}
                  {!isTrashMode && onAddToCollections && onRemoveFromCollection && (
                    <CollectionActionsMenu
                      imageId={image.id}
                      imageCollections={image.collections || '[]'}
                      collections={collections}
                      onAddToCollections={onAddToCollections}
                      onRemoveFromCollection={onRemoveFromCollection}
                      onCopyPrompt={hasPrompt ? handleCopyPrompt : undefined}
                      onDownload={handleDownload}
                      onDownloadWebP={!isVideo && !isAudio && !isText && !is3D ? handleDownloadWebP : undefined}
                      onRecreate={handleRecreate}
                      onMoveToTrash={onMoveToTrash ? handleTrashClick : undefined}
                      language={language}
                      isDownloadSuccess={downloadSuccess}
                      isWebPDownloadSuccess={webPDownloadSuccess}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Hidden context menu for right-click - rendered without button */}
        {!isTrashMode && onAddToCollections && onRemoveFromCollection && (
          <CollectionActionsMenu
            ref={contextMenuRef}
            imageId={image.id}
            imageCollections={image.collections || '[]'}
            collections={collections}
            onAddToCollections={onAddToCollections}
            onRemoveFromCollection={onRemoveFromCollection}
            onCopyPrompt={hasPrompt ? handleCopyPrompt : undefined}
            onDownload={handleDownload}
            onDownloadWebP={!isVideo && !isAudio && !isText && !is3D ? handleDownloadWebP : undefined}
            onRecreate={handleRecreate}
            onMoveToTrash={onMoveToTrash ? handleTrashClick : undefined}
            language={language}
            showButton={false}
            isDownloadSuccess={downloadSuccess}
            isWebPDownloadSuccess={webPDownloadSuccess}
          />
        )}

        {/* Image Preview Modal — only when card owns its own modal (no onImageClick) */}
        {!onImageClick && (
          <MediaPreviewModal
            isOpen={showPreview}
            slot={convertToGenerationSlot()}
            onClose={() => setShowPreview(false)}
            onToggleFavorite={handleToggleFavorite}
            onMark={onMark ? (_id, next) => onMark(image.id, next) : undefined}
            onMoveToTrash={onMoveToTrash ? (id) => onMoveToTrash(id) : undefined}
            onRecreate={handleRecreate}
            onRemoveFromCollection={onRemoveFromCollection}
            collections={collections}
            onAddToCollections={onAddToCollections}
            language={language}
          />
        )}
      </>
    );
  }

  // Grid view - always show single image, with count badge if multiple
  return (
    <div
      className={`relative rounded-lg overflow-hidden group cursor-pointer select-none ${
        isSelected ? 'ring-2 ring-purple-500 ring-offset-2 ring-offset-gray-900' : ''
      } ${
        isDragSelecting ? 'cursor-crosshair' : ''
      }`}
      onClick={handleImageClick}
      onMouseDown={handleCardMouseDown}
      onContextMenu={handleContextMenu}
      onMouseEnter={handleCardMouseEnter}
      onMouseLeave={handleCardMouseLeave}
    >
      {/* Selection checkbox - visual indicator only */}
      {selectionMode && (
        <div className="absolute top-2 left-2 z-20 pointer-events-none">
          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
            isSelected
              ? 'bg-purple-500 border-purple-500'
              : 'bg-gray-800/80 border-gray-400'
          }`}>
            {isSelected && (
              <Icon name={ICONS.CHECK} size={12} className="text-white" />
            )}
          </div>
        </div>
      )}

      {/* Media (Image, Video, Audio, or Text) - Always single item */}
      <div className="relative aspect-square bg-gray-700">
        {is3D ? (
          posterGridUrl && !imageError ? (
            <>
              <img
                src={posterGridUrl}
                alt={prompt}
                onError={() => setImageError(true)}
                className="w-full h-full object-cover"
              />
              <span className="absolute bottom-2 right-2 z-20 px-1.5 py-0.5 bg-black/60 text-cyan-300 rounded text-[10px] font-medium">
                {t.model3d?.badge || '3D'}
              </span>
            </>
          ) : (
            <Model3DPlaceholder url={model3dUrl} language={language} />
          )
        ) : imageUrl && !imageError ? (
          isVideo ? (
            <video
              ref={videoRef}
              src={imageUrl}
              className="w-full h-full object-cover"
              muted
              loop
              playsInline
              preload="metadata"
            />
          ) : isAudio ? (
            <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-purple-900/40 to-gray-800 p-3">
              <AudioWaveformPlaceholder size="medium" />
              {(() => {
                try {
                  const dur = JSON.parse(image.raw_input)?.audio_duration;
                  if (dur) {
                    const m = Math.floor(dur / 60);
                    const s = Math.round(dur % 60);
                    return <span className="mt-2 text-sm text-purple-300 font-mono">{m}:{s.toString().padStart(2, '0')}</span>;
                  }
                } catch { /* ignore */ }
                return null;
              })()}
              <p className="mt-1 text-sm text-gray-300 text-center line-clamp-3 leading-relaxed">
                {prompt}
              </p>
            </div>
          ) : (
            <img
              src={imageUrl}
              alt={prompt}
              onError={() => setImageError(true)}
              className="w-full h-full object-cover"
            />
          )
        ) : isText ? (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-700/40 to-gray-800 p-3">
            <Icon name={ICONS.TEXT} size={24} className="text-gray-400 mb-1.5 flex-shrink-0" />
            <p className="text-xs text-gray-300 text-center line-clamp-5 leading-relaxed break-words w-full px-1">
              {(() => {
                const tc = getTextContent();
                return tc || prompt;
              })()}
            </p>
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon name={isVideo ? ICONS.VIDEO : isAudio ? ICONS.AUDIO : ICONS.IMAGE} size={32} className="text-gray-500" />
          </div>
        )}
        {showHoverViewer && canShowHoverViewer && model3dUrl && (
          <div className="absolute inset-0 z-10 pointer-events-none">
            <LazyModel3DViewer
              url={model3dUrl}
              variant="card"
              poster={posterGridUrl ?? undefined}
              language={language}
            />
          </div>
        )}

        {/* Action buttons - only show on hover */}
        {image.status === 'success' && isHovered && !selectionMode && (
          <div className="absolute top-2 right-2 flex gap-1 z-10">
            {/* Restore button - only show in trash mode */}
            {isTrashMode && onRestore && (
              <button
                onClick={handleRestoreClick}
                className="p-1.5 bg-green-600/80 hover:bg-green-600 rounded-lg backdrop-blur-sm transition-colors shadow-lg"
                title={t.gallery?.restore || 'Restore'}
              >
                <Icon name={ICONS.REFRESH} size={14} className="text-white" />
              </button>
            )}

            {/* Actions Menu button - contains copy prompt, download, collections, and trash */}
            {!isTrashMode && onAddToCollections && onRemoveFromCollection && (
              <CollectionActionsMenu
                imageId={image.id}
                imageCollections={image.collections || '[]'}
                collections={collections}
                onAddToCollections={onAddToCollections}
                onRemoveFromCollection={onRemoveFromCollection}
                onCopyPrompt={hasPrompt ? handleCopyPrompt : undefined}
                onDownload={!hasMultipleImages ? handleDownload : undefined}
                onDownloadWebP={!hasMultipleImages && !isVideo && !isAudio && !isText && !is3D ? handleDownloadWebP : undefined}
                onRecreate={handleRecreate}
                onMoveToTrash={onMoveToTrash ? handleTrashClick : undefined}
                language={language}
                isDownloadSuccess={downloadSuccess}
                isWebPDownloadSuccess={webPDownloadSuccess}
              />
            )}
          </div>
        )}

        {/* Hidden context menu for right-click - rendered without button */}
        {!isTrashMode && onAddToCollections && onRemoveFromCollection && (
          <CollectionActionsMenu
            ref={contextMenuRef}
            imageId={image.id}
            imageCollections={image.collections || '[]'}
            collections={collections}
            onAddToCollections={onAddToCollections}
            onRemoveFromCollection={onRemoveFromCollection}
            onCopyPrompt={hasPrompt ? handleCopyPrompt : undefined}
            onDownload={!hasMultipleImages ? handleDownload : undefined}
            onDownloadWebP={!hasMultipleImages && !isVideo && !isAudio && !isText && !is3D ? handleDownloadWebP : undefined}
            onRecreate={handleRecreate}
            onMoveToTrash={onMoveToTrash ? handleTrashClick : undefined}
            language={language}
            showButton={false}
            isDownloadSuccess={downloadSuccess}
            isWebPDownloadSuccess={webPDownloadSuccess}
          />
        )}

        {/* Mark buttons (like / dislike / approved) - on hover, top-left.
            Top-left avoids overlapping the bottom params overlay; shifted right past the count badge on multi-image cards. */}
        {image.status === 'success' && isHovered && !selectionMode && !isTrashMode && onMark && (
          <div
            className={`absolute top-2 ${hasMultipleImages ? 'left-12' : 'left-2'} flex bg-black/60 rounded-lg backdrop-blur-sm px-1 py-0.5 z-10`}
            onClick={(e) => e.stopPropagation()}
          >
            <MarkButtons
              mark={image.mark}
              onMark={(next) => onMark(image.id, next)}
              language={language}
              size={14}
            />
          </div>
        )}

        {/* Image count badge with layers icon (shows total count if multiple images) */}
        {hasMultipleImages && (
          <div className={`absolute top-2 flex items-center gap-0.5 px-1.5 py-1 bg-purple-900/80 text-purple-300 rounded text-xs font-medium z-10 ${
            selectionMode ? 'left-9' : 'left-2'
          }`}>
            <Icon name={ICONS.LAYERS} size={14} className="text-purple-300" />
            <span>{imageCount}</span>
          </div>
        )}

        {/* Slideshow indicator dots - show on hover for multi-image cards */}
        {hasMultipleImages && isHovered && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
            {Array.from({ length: imageCount }).map((_, i) => (
              <div
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  i === slideIndex ? 'bg-white' : 'bg-white/40'
                }`}
              />
            ))}
          </div>
        )}

        {/* Compact parameter tags - show on hover at bottom-left */}
        {compactParams.length > 0 && isHovered && !selectionMode && (
          <div className={`absolute left-2 flex flex-wrap gap-1 z-10 max-w-[calc(100%-16px)] ${hasMultipleImages ? 'bottom-7' : 'bottom-2'}`}>
            {compactParams.map(param => (
              <span
                key={param.key}
                className="inline-flex items-center px-1.5 py-0.5 rounded bg-black/60 backdrop-blur-sm text-[10px] text-gray-200 whitespace-nowrap"
              >
                {param.label}: {param.value}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Image Preview Modal — only when card owns its own modal (no onImageClick) */}
      {!onImageClick && (
        <MediaPreviewModal
          isOpen={showPreview}
          slot={convertToGenerationSlot()}
          onClose={() => setShowPreview(false)}
          onToggleFavorite={handleToggleFavorite}
          onMark={onMark ? (_id, next) => onMark(image.id, next) : undefined}
          onMoveToTrash={onMoveToTrash ? (id) => onMoveToTrash(id) : undefined}
          onRecreate={handleRecreate}
          onRemoveFromCollection={onRemoveFromCollection}
          collections={collections}
          onAddToCollections={onAddToCollections}
          language={language}
        />
      )}
    </div>
  );
};

export default GalleryImageCard;
